import { useState, useEffect } from 'react';
import { Icons } from './Icons';
import { getUsers, inviteUser, deleteUser, revokeUserSessions, toggleUserStatus, UserInfo } from '../services/api';

interface UserManagementProps {
  onClose: () => void;
}

export function UserManagement({ onClose }: UserManagementProps) {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'user'>('user');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteLoading(true);
    setError(null);
    setInviteSuccess(null);

    try {
      await inviteUser(inviteEmail, inviteRole);
      setInviteSuccess(`User ${inviteEmail} added. They can now sign in.`);
      setInviteEmail('');
      loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDelete = async (user: UserInfo) => {
    if (!confirm(`Are you sure you want to remove ${user.email}? This will revoke all their sessions.`)) {
      return;
    }

    try {
      await deleteUser(user.id);
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    }
  };

  const handleRevokeSessions = async (user: UserInfo) => {
    if (!confirm(`Revoke all sessions for ${user.email}? They will need to sign in again.`)) {
      return;
    }

    try {
      await revokeUserSessions(user.id);
      alert(`All sessions revoked for ${user.email}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke sessions');
    }
  };

  const handleToggleStatus = async (user: UserInfo) => {
    const action = user.isActive ? 'disable' : 'enable';
    if (!confirm(`Are you sure you want to ${action} ${user.email}?`)) {
      return;
    }

    try {
      const result = await toggleUserStatus(user.id);
      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, isActive: result.isActive } : u
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle user status');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}
      >
        {/* Header */}
        <div
          className="p-4 flex items-center justify-between"
          style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}
        >
          <div className="flex items-center gap-3">
            <Icons.Users className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">User Management</h2>
          </div>
          <button
            onClick={onClose}
            className="btn-ghost p-2 rounded-lg hover:text-red-400"
          >
            <Icons.X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {/* Add User Form */}
          <div className="mb-6 p-4 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
            <h3 className="text-sm font-medium text-white mb-3">Add New User</h3>
            <form onSubmit={handleInvite} className="flex gap-2">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="email@company.com"
                className="flex-1 min-w-0 px-4 py-2.5 rounded-lg text-sm transition-all duration-200"
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-subtle)',
                  color: 'var(--text-primary)',
                }}
                required
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'admin' | 'user')}
                className="w-28 px-3 py-2.5 rounded-lg text-sm"
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-subtle)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              <button
                type="submit"
                disabled={inviteLoading || !inviteEmail}
                className="btn btn-primary text-sm whitespace-nowrap disabled:opacity-50"
              >
                {inviteLoading ? (
                  <Icons.Spinner className="w-4 h-4" />
                ) : (
                  <>
                    <Icons.Plus className="w-4 h-4" />
                    Add User
                  </>
                )}
              </button>
            </form>
            {inviteSuccess && (
              <p className="mt-2 text-sm text-green-400">{inviteSuccess}</p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-600/20 border border-red-600/30">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Users List */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icons.Spinner className="w-6 h-6 text-blue-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ background: 'var(--bg-tertiary)' }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-gray-500'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm">{user.email}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          user.role === 'admin'
                            ? 'bg-purple-600/30 text-purple-400'
                            : 'bg-gray-600/30 text-gray-400'
                        }`}>
                          {user.role}
                        </span>
                        {!user.isActive && (
                          <span className="text-xs px-2 py-0.5 rounded bg-red-600/30 text-red-400">
                            Disabled
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        Joined {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleRevokeSessions(user)}
                      className="btn-ghost p-2 rounded text-xs text-gray-400 hover:text-yellow-400"
                      title="Revoke all sessions"
                    >
                      <Icons.LogOut className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(user)}
                      className={`btn-ghost p-2 rounded text-xs ${
                        user.isActive ? 'text-gray-400 hover:text-orange-400' : 'text-gray-400 hover:text-green-400'
                      }`}
                      title={user.isActive ? 'Disable user' : 'Enable user'}
                    >
                      {user.isActive ? (
                        <Icons.Pause className="w-4 h-4" />
                      ) : (
                        <Icons.Play className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="btn-ghost p-2 rounded text-xs text-gray-400 hover:text-red-400"
                      title="Delete user"
                    >
                      <Icons.Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
